import React from "react";
import Card from "../../theme/untitled/Card/Card";
import Button from "../../theme/untitled/Button/Button";
import Icon from "../../theme/untitled/icons/Icon";
import FilterDropdown from "./FilterDropdown";
import MembersTable from "./MembersTable";
import MembersMobileList from "./MembersMobileList";
import { ROLE_ADMIN, ROLE_LABELS, ROLE_MEMBER, ROLE_OWNER } from "./constants";
import styles from "./MembersPage.module.scss";

const ROLE_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: ROLE_OWNER, label: ROLE_LABELS[ROLE_OWNER] },
  { value: ROLE_ADMIN, label: ROLE_LABELS[ROLE_ADMIN] },
  { value: ROLE_MEMBER, label: ROLE_LABELS[ROLE_MEMBER] },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
];

const SeatsClause = ({ seatsUsed, seatLimit, seatsFree }) => {
  // team_limit is "-1" (unlimited) on plans without a seat cap, e.g. this
  // dev org's Professional plan — not a missing-data state, so it still
  // gets the seats slot, just without a meter to render.
  if (seatLimit == null || seatLimit < 0) {
    return <span className={styles.footerSeats}>Unlimited seats</span>;
  }

  return (
    <span className={styles.footerSeats}>
      {seatsUsed} of {seatLimit} seats used
      <i>·</i>
      {seatsFree === 0 ? (
        <span className={`${styles.seatsWarn} ${styles["seatsWarn--error"]}`}>
          <Icon name="AlertCircle" size={16} />
          No seats left — add seats to invite anyone else
        </span>
      ) : seatsFree === 1 ? (
        <span className={styles.seatsWarn}>
          <Icon name="AlertCircle" size={16} />1 seat left
        </span>
      ) : (
        <span>{seatsFree} seats left</span>
      )}
      <i>·</i>
      <a href="/organization-settings/billing">Add seats</a>
    </span>
  );
};

const MembersCard = ({
  rows,
  loading,
  error,
  onRetryFetch,
  query,
  onSearchChange,
  roleFilter,
  onRoleFilterChange,
  statusFilter,
  onStatusFilterChange,
  isFiltered,
  onClearFilters,
  memberCount,
  pendingCount,
  totalCount,
  seatsUsed,
  seatLimit,
  seatsFree,
  ownerCount,
  busyRowId,
  rowError,
  onDismissRowError,
  onChangeRole,
  onResendInvite,
  onRevokeInvite,
  onOpenRemove,
  onOpenInvite,
  onOpenRolePermissions,
  inviteDisabled,
}) => {
  const countsText = isFiltered
    ? `${rows.length} of ${totalCount} ${totalCount === 1 ? "member" : "members"} shown`
    : `${memberCount} ${memberCount === 1 ? "member" : "members"} · ${pendingCount} ${
        pendingCount === 1 ? "pending invitation" : "pending invitations"
      }`;

  return (
    <Card aria-labelledby="members-title">
      <Card.Header
        id="members-title"
        title="Members"
        subtitle="Everyone with access to this workspace, and invitations waiting to be accepted."
      >
        <div className={styles.headerActions}>
          <Button
            hierarchy="secondaryGray"
            size="sm"
            iconLeading={<Icon name="Settings01" />}
            onClick={onOpenRolePermissions}
          >
            Role permissions
          </Button>
          <Button
            hierarchy="primary"
            size="sm"
            iconLeading={<Icon name="UserPlus01" />}
            onClick={onOpenInvite}
            disabled={inviteDisabled}
          >
            Invite members
          </Button>
        </div>
      </Card.Header>

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Icon name="SearchLg" size={20} className={styles.searchIcon} />
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search name or email"
            value={query}
            onChange={onSearchChange}
          />
        </div>
        <div className={styles.toolbarRight}>
          <FilterDropdown
            label="Role"
            value={roleFilter}
            onChange={onRoleFilterChange}
            options={ROLE_FILTER_OPTIONS}
          />
          <FilterDropdown
            label="Status"
            value={statusFilter}
            onChange={onStatusFilterChange}
            options={STATUS_FILTER_OPTIONS}
          />
        </div>
      </div>

      <div className={styles.desktopOnly}>
        {loading ? (
          <div className={styles.empty}>
            <p className={styles.emptyText}>Loading members…</p>
          </div>
        ) : error ? (
          <div className={styles.empty}>
            <span className={styles.emptyDisc}>
              <Icon name="AlertCircle" size={24} />
            </span>
            <h4 className={styles.emptyTitle}>Couldn't load members</h4>
            <p className={styles.emptyText}>The service didn't respond. Try again in a moment.</p>
            <div className={styles.emptyCta}>
              <Button hierarchy="secondaryGray" onClick={onRetryFetch}>
                Retry
              </Button>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className={styles.empty}>
            <span className={styles.emptyDisc}>
              <Icon name="SearchLg" size={24} />
            </span>
            <h4 className={styles.emptyTitle}>
              {isFiltered ? `No members match "${query}"` : "No members yet"}
            </h4>
            <p className={styles.emptyText}>
              {isFiltered
                ? "No active member has that name or email. The address may still be a pending invitation."
                : "Invite people to give them access to this workspace."}
            </p>
            {isFiltered && (
              <div className={styles.emptyCta}>
                <Button hierarchy="secondaryGray" onClick={onClearFilters}>
                  Clear search and filters
                </Button>
              </div>
            )}
          </div>
        ) : (
          <MembersTable
            rows={rows}
            ownerCount={ownerCount}
            busyRowId={busyRowId}
            rowError={rowError}
            onDismissRowError={onDismissRowError}
            onChangeRole={onChangeRole}
            onOpenRemove={onOpenRemove}
            onResendInvite={onResendInvite}
            onRevokeInvite={onRevokeInvite}
          />
        )}
      </div>

      <div className={styles.mobileOnly}>
        <MembersMobileList
          rows={rows}
          ownerCount={ownerCount}
          onChangeRole={onChangeRole}
          onOpenRemove={onOpenRemove}
          onResendInvite={onResendInvite}
          onRevokeInvite={onRevokeInvite}
        />
      </div>

      <div className={styles.footer}>
        <SeatsClause seatsUsed={seatsUsed} seatLimit={seatLimit} seatsFree={seatsFree} />
        <span className={styles.footerRight}>{countsText}</span>
      </div>
    </Card>
  );
};

export default MembersCard;
